// src/views/Inventory/FormPindahPos.js
import React, { useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Input,
  DatePicker,
  Button,
  Typography,
  Space,
  Modal,
  Table,
  message,
  Spin,
  Tooltip,
} from "antd";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../../configs"; // SESUAIKAN PATH
import {
  autoCorrectLotNumber,
  LOT_NUMBER_FORMAT,
} from "../../../utils/lotNumberValidator";

const { Text } = Typography;

export default function FormPindahPos() {
  const [form] = Form.useForm();

  const [saving, setSaving] = useState(false);

  // ====== STATE MODAL PILIH BARANG ======
  const [modalVisible, setModalVisible] = useState(false);
  const [itemStockLoading, setItemStockLoading] = useState(false);
  const [itemStockData, setItemStockData] = useState([]);

  // Pagination & Search state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState("");

  // Multi-select checkbox di modal (dipisah dari itemStockData agar selection
  // tetap ada saat pindah halaman / search)
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // State untuk menyimpan warning Lot Tujuan
  const [lotWarnings, setLotWarnings] = useState({});

  /**
   * Handler untuk auto-correct dan validasi Lot Tujuan saat onBlur
   */
  const handleLotTujuanBlur = (fieldIndex, value) => {
    if (!value) {
      setLotWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[fieldIndex];
        return newWarnings;
      });
      return;
    }

    const result = autoCorrectLotNumber(value);
    const warningKey = fieldIndex;

    if (result.isValid) {
      const details = form.getFieldValue("details") || [];
      if (details[fieldIndex]) {
        details[fieldIndex].lot_tujuan = result.corrected;
        form.setFieldsValue({ details });
        message.success(`Format Lot Tujuan diperbaiki: ${result.corrected}`);
      }
      setLotWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[warningKey];
        return newWarnings;
      });
    } else {
      setLotWarnings((prev) => ({
        ...prev,
        [warningKey]: result.warning,
      }));
      message.warning(`Format Lot Tujuan tidak valid: ${result.warning}`);
    }
  };

  // Load item stock ketika modal dibuka
  const fetchItemStock = async (page = 1, limit = 20, search = "") => {
    setItemStockLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;

      const res = await axiosInstance.get("/item-stock", { params });
      const list = res.data?.data || [];
      const pagination = res.data?.pagination || {};
      setItemStockData(list);
      setTotalItems(pagination.total || 0);
      setCurrentPage(pagination.page || 1);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat data stok barang");
    } finally {
      setItemStockLoading(false);
    }
  };

  const openModalPilihBarang = () => {
    setSearchKeyword("");
    setCurrentPage(1);
    setSelectedKeys([]);
    setSelectedMap({});
    setModalVisible(true);
    fetchItemStock(1, pageSize, "");
  };

  const closeModalPilihBarang = () => {
    setModalVisible(false);
  };

  // ====== TAMBAH SEMUA BARANG YANG DICENTANG KE DETAIL ======
  const mapRecordToRow = (record) => ({
    id_item_stock: record.id_item_stock,
    seri: record.seri,
    nopen: record.nopen,
    item_code: record.item_code,
    item_name: record.item_name,
    lot_asal: record.lokasi_lot,
    jumlah_kemasan_asal: record.jumlah_kemasan,
    lot_tujuan: "",
    jumlah_kemasan_tujuan: undefined,
  });

  const handleAddSelected = () => {
    const currentDetails = form.getFieldValue("details") || [];
    const alreadyAdded = new Set(currentDetails.map((r) => r.id_item_stock));

    const newRows = selectedKeys
      .map((key) => selectedMap[key])
      .filter((record) => record && !alreadyAdded.has(record.id_item_stock))
      .map(mapRecordToRow);

    if (!newRows.length) {
      message.info("Semua barang yang dipilih sudah ada di daftar");
      return;
    }

    form.setFieldsValue({ details: [...currentDetails, ...newRows] });
    setSelectedKeys([]);
    setSelectedMap({});
    message.success(`${newRows.length} barang berhasil ditambahkan`);

    const skipped = selectedKeys.length - newRows.length;
    if (skipped > 0) {
      message.warning(`${skipped} barang dilewati karena sudah ada di daftar`);
    }
  };

  // ====== SUBMIT FORM KE API /pindah-pos ======
  const handleFinish = async (values) => {
    const details = values.details || [];

    if (!details.length) {
      message.error("Minimal harus ada satu baris detail pindah pos");
      return;
    }

    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    const values = pendingValues;
    const details = values.details || [];
    const nowIso = new Date().toISOString();

    const headerPayload = {
      id_pindah_lot: 0,
      no_pindah_lot: values.no_pindah_lot,
      tanggal_pindah_lot: values.tanggal_pindah_lot
        ? dayjs(values.tanggal_pindah_lot).format("YYYY-MM-DD")
        : null,
      catatan: values.catatan || 0,
      created_by: "1",
      created_at: nowIso,
    };

    const detailsPayload = details.map((row, idx) => ({
      id_pindah_lot_detail: 0,
      id_pindah_lot: 0,
      seri: row.seri || idx + 1,
      nopen: row.nopen,
      item_code: row.item_code,
      lot_asal: row.lot_asal,
      jumlah_kemasan_asal: Number(row.jumlah_kemasan_asal) || 0,
      lot_tujuan: row.lot_tujuan,
      jumlah_kemasan_tujuan: Number(row.jumlah_kemasan_tujuan) || 0,
      created_by: "1",
      created_at: nowIso,
    }));

    const payload = {
      header: headerPayload,
      details: detailsPayload,
    };

    try {
      setSaving(true);
      await axiosInstance.post("/pindah-lot", payload);
      message.success("Data pindah pos / pindah lot berhasil disimpan");

      form.resetFields();
      setConfirmVisible(false);
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan data pindah pos");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    window.history.back();
  };

  // ====== KOLOM TABLE MODAL ITEM-STOCK ======
  const itemStockColumns = [
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
      ellipsis: { showTitle: false },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: "Item Code",
      dataIndex: "item_code",
      key: "item_code",
    },
    {
      title: "Nama Barang",
      dataIndex: "item_name",
      key: "item_name",
      ellipsis: { showTitle: false },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: "Seri",
      dataIndex: "seri",
      key: "seri",
      width: 80,
    },
    {
      title: "Nopen",
      dataIndex: "nopen",
      key: "nopen",
      width: 120,
    },
    {
      title: "Tanggal Nopen",
      dataIndex: "tanggal_nopen",
      key: "tanggal_nopen",
      width: 130,
      render: (text, record) => {
        const date = record.tanggal_nopen || record.document_date;
        return date ? dayjs(date).format("DD-MM-YYYY") : "-";
      },
    },
    {
      title: "Jumlah Kemasan",
      dataIndex: "jumlah_kemasan",
      key: "jumlah_kemasan",
      width: 140,
    },
    {
      title: "Lot / Lokasi",
      dataIndex: "lokasi_lot",
      key: "lokasi_lot",
      width: 120,
    },
  ];

  return (
    <>
      <Card
        className="kt-portlet"
        bodyStyle={{ padding: 24 }}
        style={{
          borderRadius: 16,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          border: "1px solid #F3F6F9",
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{
            tanggal_pindah_lot: dayjs(),
            details: [],
          }}
        >
          {/* ===== HEADER TITLE ===== */}
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 16 }}
          >
            <Col>
              <Typography.Title level={3} style={{ marginBottom: 4 }}>
                Form Pindah Pos / Pindah Lot
              </Typography.Title>
              <Text type="secondary">
                Catat perpindahan posisi barang antar lot / rak.
              </Text>
            </Col>
          </Row>

          {/* ===== HEADER FORM ===== */}
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label={<Text strong>No Pindah Lot</Text>}
                name="no_pindah_lot"
                rules={[
                  { required: true, message: "No Pindah Lot wajib diisi" },
                ]}
              >
                <Input placeholder="Misal: PDL-251129001" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label={<Text strong>Tanggal Pindah Lot</Text>}
                name="tanggal_pindah_lot"
                rules={[
                  { required: true, message: "Tanggal pindah wajib diisi" },
                ]}
              >
                <DatePicker style={{ width: "100%" }} format="DD MMMM YYYY" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item label={<Text strong>Catatan</Text>} name="catatan">
                <Input placeholder="Catatan pindah lot (opsional)" />
              </Form.Item>
            </Col>
          </Row>

          {/* ===== TABEL DETAIL PINDAH POS ===== */}
          <Card
            bodyStyle={{ padding: 0 }}
            style={{
              marginTop: 8,
              borderRadius: 4,
              border: "1px solid #E4E6EF",
              overflow: "hidden",
            }}
          >
            {/* Header kolom ala Metronic */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "0.6fr 1.4fr 2.2fr 1.4fr 1.8fr 1.4fr 1.8fr 0.8fr",
                background: "#F3F6F9",
                padding: "8px 16px",
                fontWeight: 600,
                fontSize: 13,
                alignItems: "center",
              }}
            >
              <div>SERI</div>
              <div>NOPEN</div>
              <div>ITEM CODE</div>
              <div>LOT ASAL</div>
              <div>JML KEMASAN ASAL</div>
              <div>LOT TUJUAN</div>
              <div>JML KEMASAN TUJUAN</div>
              <div style={{ textAlign: "center" }}>AKSI</div>
            </div>

            <Form.List name="details">
              {(fields, { remove }) => (
                <>
                  {fields.map((field, index) => {
                    const row =
                      (form.getFieldValue("details") || [])[field.name] || {};
                    return (
                      <div
                        key={field.key}
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "0.6fr 1.4fr 2.2fr 1.4fr 1.8fr 1.4fr 1.8fr 0.8fr",
                          padding: "8px 16px",
                          borderTop: "1px solid #E4E6EF",
                          alignItems: "center",
                        }}
                      >
                        {/* SERI (dibaca saja dari field.seri) */}
                        <Form.Item
                          {...field}
                          name={[field.name, "seri"]}
                          fieldKey={[field.fieldKey, "seri"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input disabled placeholder="Seri" />
                        </Form.Item>

                        {/* NOPEN */}
                        <Form.Item
                          {...field}
                          name={[field.name, "nopen"]}
                          fieldKey={[field.fieldKey, "nopen"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input disabled placeholder="Nopen" />
                        </Form.Item>

                        {/* ITEM CODE + NAMA BARANG (tampilan saja, data dari row) */}
                        <div style={{ paddingRight: 8 }}>
                          <div style={{ lineHeight: 1.4 }}>
                            {row.item_code || "-"}
                          </div>
                          {row.item_name && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#B5B5C3",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.item_name}
                            </div>
                          )}
                        </div>

                        {/* LOT ASAL */}
                        <Form.Item
                          {...field}
                          name={[field.name, "lot_asal"]}
                          fieldKey={[field.fieldKey, "lot_asal"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input disabled placeholder="Lot Asal" />
                        </Form.Item>

                        {/* JML KEMASAN ASAL */}
                        <Form.Item
                          {...field}
                          name={[field.name, "jumlah_kemasan_asal"]}
                          fieldKey={[field.fieldKey, "jumlah_kemasan_asal"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input
                            disabled
                            type="number"
                            placeholder="Jumlah Kemasan Asal"
                          />
                        </Form.Item>

                        {/* LOT TUJUAN (editable) */}
                        <Form.Item
                          {...field}
                          name={[field.name, "lot_tujuan"]}
                          fieldKey={[field.fieldKey, "lot_tujuan"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                          rules={[
                            {
                              required: true,
                              message: "Lot tujuan wajib diisi",
                            },
                          ]}
                          help={
                            lotWarnings[field.name] ? (
                              <span style={{ color: "#faad14", fontSize: 11 }}>
                                ⚠️ {lotWarnings[field.name]}
                              </span>
                            ) : null
                          }
                          validateStatus={
                            lotWarnings[field.name] ? "warning" : undefined
                          }
                          tooltip={LOT_NUMBER_FORMAT.tooltip}
                        >
                          <Input
                            placeholder={LOT_NUMBER_FORMAT.placeholder}
                            onBlur={(e) =>
                              handleLotTujuanBlur(field.name, e.target.value)
                            }
                            style={
                              lotWarnings[field.name]
                                ? {
                                    borderColor: "#faad14",
                                    backgroundColor: "#fffbe6",
                                  }
                                : undefined
                            }
                          />
                        </Form.Item>

                        {/* JML KEMASAN TUJUAN (editable) */}
                        <Form.Item
                          {...field}
                          name={[field.name, "jumlah_kemasan_tujuan"]}
                          fieldKey={[field.fieldKey, "jumlah_kemasan_tujuan"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                          rules={[
                            {
                              required: true,
                              message: "Jumlah kemasan tujuan wajib diisi",
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            placeholder="Jumlah Kemasan Tujuan"
                          />
                        </Form.Item>

                        {/* AKSI - HAPUS ROW */}
                        <div style={{ textAlign: "center" }}>
                          <Button
                            type="text"
                            icon={
                              <DeleteOutlined style={{ color: "#F64E60" }} />
                            }
                            onClick={() => remove(field.name)}
                          />
                        </div>
                      </div>
                    );
                  })}

                  {/* Jika belum ada detail */}
                  {fields.length === 0 && (
                    <div
                      style={{
                        padding: "12px 16px",
                        borderTop: "1px solid #E4E6EF",
                        color: "#B5B5C3",
                        fontStyle: "italic",
                      }}
                    >
                      Belum ada barang yang dipindahkan. Klik{" "}
                      <b>Pilih Barang</b> untuk menambah.
                    </div>
                  )}
                </>
              )}
            </Form.List>
          </Card>

          {/* ===== ACTIONS BAWAH TABEL ===== */}
          <Row justify="space-between" align="middle" style={{ marginTop: 16 }}>
            <Col>
              <Button icon={<SearchOutlined />} onClick={openModalPilihBarang}>
                Pilih Barang dari Stok
              </Button>
            </Col>
            <Col>
              <Space>
                <Button onClick={handleCancel}>Batal</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  style={{
                    background: "#1BC5BD",
                    borderColor: "#1BC5BD",
                    minWidth: 140,
                    fontWeight: 600,
                  }}
                >
                  Simpan
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* ===== MODAL PILIH BARANG DARI ITEM-STOCK ===== */}
      <Modal
        title="Pilih Barang Dari Stok"
        open={modalVisible}
        onCancel={closeModalPilihBarang}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={closeModalPilihBarang}>Tutup</Button>
            <Button
              type="primary"
              disabled={selectedKeys.length === 0}
              onClick={handleAddSelected}
              style={{
                background: "#1BC5BD",
                borderColor: "#1BC5BD",
                fontWeight: 600,
              }}
            >
              Tambah ke Daftar ({selectedKeys.length})
            </Button>
          </div>
        }
        width={1000}
        destroyOnClose
      >
        {/* Search Bar */}
        <Input.Search
          placeholder="Cari item code, customer, nopen, atau lokasi lot..."
          allowClear
          enterButton="Cari"
          value={searchKeyword}
          onChange={(e) => {
            const val = e.target.value;
            setSearchKeyword(val);
          }}
          onSearch={(val) => {
            setCurrentPage(1);
            fetchItemStock(1, pageSize, val);
          }}
          style={{ marginBottom: 16 }}
        />

        <Table
          rowKey="id_item_stock"
          rowSelection={{
            selectedRowKeys: selectedKeys,
            preserveSelectedRowKeys: true,
            onChange: (keys, rows) => {
              const nextMap = { ...selectedMap };
              itemStockData.forEach((record) => {
                if (!keys.includes(record.id_item_stock))
                  delete nextMap[record.id_item_stock];
              });
              rows.forEach((record) => {
                nextMap[record.id_item_stock] = record;
              });
              setSelectedKeys(keys);
              setSelectedMap(nextMap);
            },
          }}
          loading={itemStockLoading}
          columns={itemStockColumns}
          dataSource={itemStockData}
          size="small"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} item`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
              fetchItemStock(page, size, searchKeyword);
            },
          }}
          scroll={{ x: 800 }}
        />
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        title="Konfirmasi Pindah Lot"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        {pendingValues && (
          <div>
            <p>Apakah Anda yakin ingin menyimpan data pindah lot ini?</p>
            <div
              style={{
                background: "#f5f5f5",
                padding: 10,
                borderRadius: 4,
                marginBottom: 16,
              }}
            >
              <p style={{ margin: 0 }}>
                <strong>No Pindah Lot:</strong> {pendingValues.no_pindah_lot}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Tanggal:</strong>{" "}
                {pendingValues.tanggal_pindah_lot
                  ? pendingValues.tanggal_pindah_lot.format("DD MMMM YYYY")
                  : "-"}
              </p>
            </div>

            <Text strong>Detail Barang:</Text>
            <div
              style={{
                maxHeight: 200,
                overflowY: "auto",
                marginTop: 8,
                border: "1px solid #f0f0f0",
              }}
            >
              <table
                style={{
                  width: "100%",
                  fontSize: 12,
                  borderCollapse: "collapse",
                }}
              >
                <thead
                  style={{ background: "#fafafa", position: "sticky", top: 0 }}
                >
                  <tr>
                    <th
                      style={{
                        padding: "4px 8px",
                        textAlign: "left",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Item Code
                    </th>
                    <th
                      style={{
                        padding: "4px 8px",
                        textAlign: "left",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Lot Asal
                    </th>
                    <th
                      style={{
                        padding: "4px 8px",
                        textAlign: "left",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Lot Tujuan
                    </th>
                    <th
                      style={{
                        padding: "4px 8px",
                        textAlign: "right",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      Jml
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(pendingValues.details || []).map((d, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "4px 8px" }}>{d.item_code}</td>
                      <td style={{ padding: "4px 8px" }}>{d.lot_asal}</td>
                      <td style={{ padding: "4px 8px" }}>{d.lot_tujuan}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>
                        {d.jumlah_kemasan_tujuan}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
